import React, { useMemo, type ReactNode } from 'react';

import { useHubTheming } from '../../controllers/hub-theming-context';
import { useIsHubSpaceDensityEnabled } from '../../controllers/use-is-hub-space-density-enabled';
import {
	DEFAULT_SPACE_DENSITY,
	EDITOR_BLOCK_SPACING_VAR,
	getBlockSpacingForDensity,
} from '../../utils/hub-space-density/types';

/**
 * Applies the picked space density (the vertical rhythm between hub extensions)
 * to admin-published hub content in view mode.
 *
 * The hub's own renderer wrapper (`HubRendererWrapper`) reads
 * `var(--ak-editor-extension-block-spacing, 0)` as top/bottom padding on each
 * bespoke hub extension, so all this component needs to do is SET that custom
 * property on an ancestor.
 *
 * The property is set on a `display: contents` wrapper (no box of its own, so it
 * never disturbs the ancestor layout). Inline JSX only — no `useEffect`, no DOM
 * mutation — so it is SSR-safe. When the gate is off, no density is set, or the
 * host disables the surface, children render verbatim (a strict no-op).
 *
 * @param enabled - Host-supplied gate for WHERE spacing applies. In view mode
 *   this is wired to the dynamic hub pages (home, custom, portal); the static
 *   editors (request create/details) never render this scope. Combined with the
 *   space-density feature gate and a persisted/selected density. When any is
 *   false, children render verbatim.
 */
export const HubSpacingScope = ({
	children,
	enabled = true,
}: {
	children: ReactNode;
	enabled?: boolean;
}): JSX.Element => {
	const gateEnabled = useIsHubSpaceDensityEnabled();
	const theming = useHubTheming();
	const spaceDensity = theming?.spaceDensity;

	const wrapperStyle = useMemo<React.CSSProperties>(() => {
		const density = spaceDensity ?? DEFAULT_SPACE_DENSITY;
		return {
			display: 'contents',
			[EDITOR_BLOCK_SPACING_VAR]: getBlockSpacingForDensity(density),
		} as unknown as React.CSSProperties;
	}, [spaceDensity]);

	// No-op unless the feature gate is on, the host enables this surface, and a
	// density has actually been selected/persisted.
	if (!gateEnabled || !enabled || !spaceDensity) {
		return <>{children}</>;
	}

	return (
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
		<div data-hub-space-density-scope="" style={wrapperStyle}>
			{children}
		</div>
	);
};
